import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { showErrorMessage } from "@/common/components/ErrorMessage";
import PropTypes from "prop-types";

const { Customer } = Mobi2Go;

function LoyaltyOptInCheckbox({ ViewComponent, optInString, optInLink, CustomHtmlSlot }) {
    const [isCustomerOptedIn, setIsCustomerOptedIn] = useState(false);
    const [checkboxDisabled, setCheckboxDisabled] = useState(false);
    const [hasJustOptedIn, setHasJustOptedIn] = useState(false);
    const [checkboxState, setCheckboxState] = useState(false);

    const [t] = useTranslation('customer');
    const successTimerDuration = 20000;

    LoyaltyOptInCheckbox.defaultProps = {
        optInLink: '',
        CustomHtmlSlot: null,
    }

    useEffect(() => {
        const updateCustomerState = () => {
            setIsCustomerOptedIn(Customer.getLoyaltyOptIn());
        }

        const updateRecentlyOptedIn = () => {
            setHasJustOptedIn(true);
        }

        updateCustomerState();

        Customer.bind({
            updateLoyalty: updateCustomerState,
            login: updateCustomerState,
            logout: updateCustomerState,
            recentlyOptedInToLoyalty: updateRecentlyOptedIn
        });
        return () => {
            Customer.unbind({
                updateLoyalty: updateCustomerState,
                login: updateCustomerState,
                logout: updateCustomerState,
                recentlyOptedInToLoyalty: updateRecentlyOptedIn
            });
        }
    }, []);

    useEffect(() => {
        if (hasJustOptedIn) {
            // We want to display a success message for a limited time to indicate that the user has successfully opted in.
            // By delaying when we trigger updateLoyalty we allow a temporary success message to be displayed.
            const successTimer = setTimeout(() => Customer.trigger('updateLoyalty'), successTimerDuration);
            return () => {
                // The component was unmounted, clear the timer and trigger the updateLoyalty event
                clearTimeout(successTimer);
                Customer.trigger('updateLoyalty');
            }
        }
    }, [hasJustOptedIn]);

    const handleSubmit = async () => {
        setCheckboxState(true);
        setCheckboxDisabled(true);

        try {
            await Customer.requestLoyaltyOptIn();
            // By triggering this event we keep all other instances of this component in sync
            Customer.trigger('recentlyOptedInToLoyalty');
        } catch (err) {
            console.log(err);
            showErrorMessage(t('loyalty_opt_in_error_title'), t('loyalty_opt_in_error_message'));
        } finally {
            setCheckboxDisabled(false);
            setCheckboxState(false);
        }
    }

    // If Customer is opted in we don't show anything here
    if (isCustomerOptedIn) {
        return null;
    }

    return (
        // The ViewComponent allows us to use a different implementation of the checkbox for different views
        // without having to duplicate the state management and business logic
        <ViewComponent
            hasJustOptedIn={hasJustOptedIn}
            checkboxState={checkboxState}
            checkboxDisabled={checkboxDisabled}
            handleSubmit={handleSubmit}
            optInString={optInString}
            optInLink={optInLink}
            successMessage={t('order.confirmation:loyalty_opted_in_message')}
            CustomHtmlSlot={CustomHtmlSlot}
        />
    );
}

LoyaltyOptInCheckbox.propTypes = {
    ViewComponent: PropTypes.elementType.isRequired,
    optInString: PropTypes.string.isRequired,
    optInLink: PropTypes.string,
    CustomHtmlSlot: PropTypes.elementType,
}

export default LoyaltyOptInCheckbox;
